import React, { Component } from 'react';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { withStyles } from '@mui/styles';
import { useNotify } from 'react-admin';
import { sortBy } from 'lodash';
import phase from '../../../constants/phase';
import dataProvider from '../../../providers/dataProvider';
import SessionExercisesTable from './SessionExercisesTable';
import { sumOfCol, convertToMinuts } from './utilsSessionExercise';
import SumCell from './SumCell';
import normalizeDurationOptions from '../normalizeDurationOptions';

const style = {
  sumCellTitle: {
    textAlign: 'right',
    backgroundColor: '#d4d4d4',
  },
  sumCellValue: {
    backgroundColor: '#d4d4d4',
  },
};

class SessionExercises extends Component {
  constructor(props) {
    super(props);
    this.state = {
      exercisesAll: [],
      exercises: {
        prep: [],
        practice: [],
        play: [],
        push: [],
      },
      loading: false,
    };
  }

  componentDidMount() {
    this.getRelation();
  }

  groupByPhase = (exercisesAll) => ({
    prep: sortBy(
      exercisesAll.filter((item) => item.phase === phase.prepare),
      (item) => item.order,
    ),
    practice: sortBy(
      exercisesAll.filter((item) => item.phase === phase.practice),
      (item) => item.order,
    ),
    play: sortBy(
      exercisesAll.filter((item) => item.phase === phase.play),
      (item) => item.order,
    ),
    push: sortBy(
      exercisesAll.filter((item) => item.phase === phase.push),
      (item) => item.order,
    ),
  });

  getRelation = () => {
    this.setState({ loading: true });
    return dataProvider('GET_ALL', `sessions/${this.props.values.id}/relation`, {}).then((res) => {
      this.setState({
        loading: false,
        exercisesAll: res.data,
        exercises: this.groupByPhase(res.data),
      });
    });
  };

  // timeMatrix is mutated in place, so hand the tables fresh arrays to pull them off
  // their PureComponent bailout and recompute the duration totals.
  refreshTotals = () => {
    this.setState((prevState) => {
      const exercisesAll = [...prevState.exercisesAll];
      return { exercisesAll, exercises: this.groupByPhase(exercisesAll) };
    });
  };

  addExercises = async (event) => {
    this.setState({ loading: true });
    try {
      // Process exercises sequentially using reduce
      await event.reduce(
        (promise, item) =>
          promise.then(() =>
            dataProvider('CREATE', `sessions/${this.props.values.id}/relation`, {
              data: item,
            }),
          ),
        Promise.resolve(),
      );

      await this.getRelation();
    } finally {
      this.setState({ loading: false });
    }
  };

  addCircuit = (values) => {
    const durationOptions = normalizeDurationOptions(this.props.values.durationOptions);

    values.type = 'circuit';
    values.timeMatrix = {};

    for (const key in durationOptions) {
      values.timeMatrix[key.toString()] = 1;
    }

    this.setState({ loading: true });

    dataProvider('CREATE', `sessions/${this.props.values.id}/relation`, {
      data: values,
    }).finally(() => {
      this.getRelation();
      this.setState({ loading: false });
    });
  };

  updateCircuit = (circuitId, values) => {
    this.setState({ loading: true });

    const data = {
      name: values.name,
      restFor: values.restFor,
    };

    dataProvider('UPDATE', `sessions/${this.props.values.id}/relation`, {
      id: circuitId,
      data,
    }).finally(() => {
      this.getRelation();
      this.setState({ loading: false });
    });
  };

  updateCell = (item, cell, value) => {
    const previousValue = item.timeMatrix[cell];
    item.timeMatrix[cell] = value;

    return dataProvider('UPDATE', `sessions/${this.props.values.id}/relation`, {
      id: item.id,
      data: { ...item, exerciseId: item.exercise ? item.exercise.id : null },
    })
      .then(() => {
        this.refreshTotals();
      })
      .catch((error) => {
        item.timeMatrix[cell] = previousValue;
        this.refreshTotals();
        this.props.notify(error?.message || error || 'Failed to save exercise time', {
          type: 'error',
        });
        throw error;
      });
  };

  handleDuplicate = (row) => {
    this.setState({ loading: true });
    dataProvider(
      'CREATE',
      `sessions/${this.props.values.id}/relation/${row.id}/duplicate`,
      {},
    ).finally(() => {
      this.getRelation();
      this.setState({ loading: false });
    });
  };

  handleDelete = (cell) => {
    this.setState({ loading: true });
    dataProvider('DELETE', `sessions/${this.props.values.id}/relation`, {
      id: cell.id,
    }).then(() => {
      this.setState({ loading: false });
      this.getRelation();
    });
  };

  handleUp = (row, list, index) => {
    this.setState({ loading: true });
    const exr1 = row;
    const exr2 = list[index - 1];
    const order = index + 1;
    exr1.order = order - 1;
    exr1.exerciseId = exr1.exercise ? exr1.exercise.id : null;
    exr2.order = order;
    exr2.exerciseId = exr2.exercise ? exr2.exercise.id : null;
    const requests = [
      dataProvider('UPDATE', `sessions/${this.props.values.id}/relation`, {
        id: exr1.id,
        data: exr1,
      }),
      dataProvider('UPDATE', `sessions/${this.props.values.id}/relation`, {
        id: exr2.id,
        data: exr2,
      }),
    ];
    Promise.all(requests).then(() => {
      this.setState({ loading: false });
      this.getRelation();
    });
  };

  handleDown = (row, list, index) => {
    this.setState({ loading: true });
    const exr1 = row;
    const exr2 = list[index + 1];
    const order = index + 1;
    exr1.order = order + 1;
    exr1.exerciseId = exr1.exercise ? exr1.exercise.id : null;
    exr2.order = order;
    exr2.exerciseId = exr2.exercise ? exr2.exercise.id : null;
    const requests = [
      dataProvider('UPDATE', `sessions/${this.props.values.id}/relation`, {
        id: exr1.id,
        data: exr1,
      }),
      dataProvider('UPDATE', `sessions/${this.props.values.id}/relation`, {
        id: exr2.id,
        data: exr2,
      }),
    ];
    Promise.all(requests).then(() => {
      this.setState({ loading: false });
      this.getRelation();
    });
  };

  handleUpdatePhaseDescription = (phaseId, description) => {
    dataProvider('UPDATE', `sessions/${this.props.values.id}/phase`, {
      id: phaseId,
      data: { description },
    });
  };

  getSummaryDuration = (cell, exercises, withVideo) => {
    return exercises
      .map((item) => {
        let sum = 0;

        if (item.type == 'circuit') {
          sum = this.getSummaryDuration(cell, item.sessionExercises, withVideo);
          sum *= item.timeMatrix[cell] || 1;
          if (sum) {
            sum += item.restFor;
          }
        } else {
          sum = sumOfCol(item, cell);
          if (withVideo && item.exercise) {
            const { duration } = item.exercise.video;
            if (duration) {
              sum += parseInt(duration, 10);
            }
          }
        }
        return sum;
      })
      .reduce((acc, curent) => +acc + +curent, 0);
  };

  getPhaseDescription = (phaseId) => {
    const { phasesInfo } = this.props.values;
    if (phasesInfo && phasesInfo) {
      return phasesInfo.filter((item) => item.phase === phaseId)[0]?.description ?? undefined;
    }

    return undefined;
  };

  render() {
    const { values, classes } = this.props;
    const { exercises, loading, exercisesAll } = this.state;
    const durationOptions = normalizeDurationOptions(values?.durationOptions);
    return (
      <>
        <Button onClick={this.getRelation} color="primary" className={classes.button}>
          Update Totals
        </Button>
        <SessionExercisesTable
          title="Prep"
          description={this.getPhaseDescription(phase.prepare)}
          exercises={exercises.prep}
          phase={phase.prepare}
          durationOptions={durationOptions}
          addExercises={this.addExercises}
          updateCell={this.updateCell}
          handleDelete={this.handleDelete}
          handleDuplicate={this.handleDuplicate}
          handleUp={this.handleUp}
          handleDown={this.handleDown}
          loading={loading}
          addCircuit={this.addCircuit}
          updateCircuit={this.updateCircuit}
          getSummaryDuration={this.getSummaryDuration}
          handleUpdatePhaseDescription={this.handleUpdatePhaseDescription}
        />
        <SessionExercisesTable
          title="Practice"
          description={this.getPhaseDescription(phase.practice)}
          exercises={exercises.practice}
          phase={phase.practice}
          durationOptions={durationOptions}
          addExercises={this.addExercises}
          updateCell={this.updateCell}
          handleDelete={this.handleDelete}
          handleDuplicate={this.handleDuplicate}
          handleUp={this.handleUp}
          handleDown={this.handleDown}
          loading={loading}
          addCircuit={this.addCircuit}
          updateCircuit={this.updateCircuit}
          getSummaryDuration={this.getSummaryDuration}
          handleUpdatePhaseDescription={this.handleUpdatePhaseDescription}
        />
        <SessionExercisesTable
          title="Play"
          description={this.getPhaseDescription(phase.play)}
          exercises={exercises.play}
          phase={phase.play}
          durationOptions={durationOptions}
          addExercises={this.addExercises}
          updateCell={this.updateCell}
          handleDelete={this.handleDelete}
          handleDuplicate={this.handleDuplicate}
          handleUp={this.handleUp}
          handleDown={this.handleDown}
          loading={loading}
          addCircuit={this.addCircuit}
          updateCircuit={this.updateCircuit}
          getSummaryDuration={this.getSummaryDuration}
          handleUpdatePhaseDescription={this.handleUpdatePhaseDescription}
        />
        <SessionExercisesTable
          title="Push"
          description={this.getPhaseDescription(phase.push)}
          exercises={exercises.push}
          phase={phase.push}
          durationOptions={durationOptions}
          addExercises={this.addExercises}
          updateCell={this.updateCell}
          handleDelete={this.handleDelete}
          handleDuplicate={this.handleDuplicate}
          handleUp={this.handleUp}
          handleDown={this.handleDown}
          loading={loading}
          addCircuit={this.addCircuit}
          updateCircuit={this.updateCircuit}
          getSummaryDuration={this.getSummaryDuration}
          handleUpdatePhaseDescription={this.handleUpdatePhaseDescription}
        />
        {Object.keys(durationOptions).length && this.state.exercisesAll.length ? (
          <Table>
            <TableBody>
              <TableRow>
                <TableCell style={{ width: '300px' }} className={classes.sumCellTitle}>
                  TOTAL SESSION EXERCISE TIME
                </TableCell>
                {Object.keys(durationOptions).map((cell, i) => {
                  const summaryDuration = this.getSummaryDuration(cell, exercisesAll);
                  return (
                    <TableCell className={classes.sumCellValue} key={i} align="left">
                      <SumCell {...convertToMinuts(summaryDuration)} />
                    </TableCell>
                  );
                })}
                <TableCell className={classes.sumCellTitle} />
              </TableRow>
              <TableRow>
                <TableCell style={{ width: '300px' }} className={classes.sumCellTitle}>
                  TOTAL SESSION TIME INCLUDING VIDEO
                </TableCell>
                {Object.keys(durationOptions).map((cell, i) => {
                  const summaryDuration = this.getSummaryDuration(cell, exercisesAll, true);
                  return (
                    <TableCell className={classes.sumCellValue} key={i} align="left">
                      <SumCell {...convertToMinuts(summaryDuration)} />
                    </TableCell>
                  );
                })}
                <TableCell className={classes.sumCellTitle} />
              </TableRow>
            </TableBody>
          </Table>
        ) : null}
      </>
    );
  }
}

const StyledSessionExercises = withStyles(style)(SessionExercises);

const SessionExercisesWithNotify = (props) => {
  const notify = useNotify();
  return <StyledSessionExercises {...props} notify={notify} />;
};

export default SessionExercisesWithNotify;
